/**
 * @file: FeedThreadView.tsx
 *
 * Detail view for a single feed thread — navigation-agnostic embeddable
 * content. Takes the thread identity as props (the host reads them from a
 * route or a desktop selection) and lists messages newest-first (matches
 * server ordering). On mount it marks the thread read (the inbox
 * optimistically did this on tap, but deep-link entries also need it).
 * `embedded` renders the desktop PaneHeader; on a mobile stack the navigator
 * supplies the header instead.
 */

import { Typography } from '@euler/app/components/typography';
import { useOpenOrderDetail } from '@euler/app/flows/common/functions/useOpenOrderDetail';
import { TimelineMessage } from '@euler/app/flows/feed/components/TimelineMessage';
import { useFeedUnreadStore } from '@euler/app/flows/feed/lib/unread/context';
import { threadUsesManualRead } from '@euler/app/flows/feed/utils/feedThreadDescriptor';
import { useInboxContext } from '@euler/app/flows/inbox/context';
import { EmptyView } from '@euler/components/EmptyView';
import { PaneHeader } from '@euler/components/PaneHeader';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FeedInboxItem, FeedThreadScope } from '@euler/model/feed';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { ActionSheetProvider } from '@expo/react-native-action-sheet';
import { useNavigation } from '@react-navigation/native';
import { FC, memo, useEffect, useLayoutEffect } from 'react';
import {
  ActivityIndicator,
  FlatList,
  ListRenderItem,
  RefreshControl,
  TouchableOpacity,
  View,
} from 'react-native';
import { useFeedThreadMessages } from '../functions/useFeedThreadMessages';

export const FeedThreadView: FC<{
  threadId: string;
  /** Drives the maintenance-plan "manual read" rule (see below). */
  scope: FeedThreadScope;
  /** Thread's `scope_id` — the order no for `order` threads. */
  scopeId?: string;
  title?: string;
  /** Desktop master-detail pane: render a PaneHeader (mobile uses the nav header). */
  embedded?: boolean;
}> = memo(({ threadId, scope, scopeId, title, embedded }) => {
  const { rpx } = useResponsiveContext();
  const { feedService } = useServiceFactory();
  const { refreshFeedUnread } = useFeedUnreadStore();
  const inbox = useInboxContext();
  const navigation = useNavigation();
  const openOrderDetail = useOpenOrderDetail();

  // Order threads carry a header-right link to the order: the in-progress task
  // while unfinished, the service record once finished. Mobile only — the
  // desktop pane renders its own PaneHeader, not the stack nav header.
  useLayoutEffect(() => {
    if (embedded || scope !== FeedThreadScope.Order || !scopeId) return;
    navigation.setOptions({
      headerRight: () => (
        <TouchableOpacity
          onPress={() => void openOrderDetail(scopeId)}
          hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
          style={{ marginRight: rpx(14) }}
        >
          <Typography.Text size={rpx(14)} color="#0066ff" medium>
            查看订单
          </Typography.Text>
        </TouchableOpacity>
      ),
    });
  }, [embedded, scope, scopeId, navigation, openOrderDetail, rpx]);

  const {
    messages,
    loading,
    refreshing,
    hasMore,
    loadingMore,
    refresh,
    loadMore,
    markRead,
    applyRemindersRead,
  } = useFeedThreadMessages(threadId);

  useEffect(() => {
    // Best-effort mark read on entry. Optimistic burger badge update
    // already happened on inbox tap; this catches deep-link entries.
    // Maintenance-plan threads are exempt — their reminders stay unread
    // until the user acts on each one (mark-read / convert-to-lead).
    if (threadUsesManualRead(scope)) return;
    feedService
      .markThreadRead(threadId)
      .catch(onErrorIgnore)
      .finally(() => {
        void refreshFeedUnread();
      });
    // mark-read once per thread mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMarkRead = usePersistFn(async (recipientId: string) => {
    await markRead(recipientId);
    void refreshFeedUnread();
    // Desktop master-detail keeps the thread list mounted beside this
    // pane, so nudge it to refetch the row's unread badge. `useInbox
    // Context` is null outside the inbox (mobile standalone screen),
    // where the list self-refreshes on focus instead.
    const tick$ = inbox?.feedListReloadTick$;
    tick$?.next(tick$.getValue() + 1);
  });

  // After a convert-to-lead bundle, flip every bundled item's reminder read
  // (not just the tapped one) + nudge the inbox row. The server write and the
  // badge refresh belong to the conversion itself (`LeadAffordanceButton`),
  // which fires from the plans timeline too.
  const onConvertedToLead = usePersistFn((planDetailIds: number[]) => {
    applyRemindersRead(planDetailIds);
    const tick$ = inbox?.feedListReloadTick$;
    tick$?.next(tick$.getValue() + 1);
  });

  const renderItem: ListRenderItem<FeedInboxItem> = ({ item, index }) => (
    <TimelineMessage
      message={item}
      isFirst={index === 0}
      isLast={index === messages.length - 1 && !hasMore}
      onMarkRead={onMarkRead}
      onConvertedToLead={onConvertedToLead}
    />
  );

  return (
    // Scope the action sheet (maintenance-plan card three-dots menu) to
    // this pane. Without a local provider it resolves to the app-root one
    // and the sheet spans the whole desktop window instead of the thread
    // detail column.
    <ActionSheetProvider>
      <View
        style={{
          flex: 1,
          // No bottom inset: pushed, this is a bottom-tab scene whose tab bar
          // already pads the home indicator; embedded, the desktop pane has no
          // inset at all. Adding it leaves a dead strip under the timeline.
          backgroundColor: '#ffffff',
        }}
      >
        {embedded ? <PaneHeader title={title ?? '消息'} /> : null}
        {loading && messages.length === 0 ? (
          <View
            css={`
              flex: 1;
              align-items: center;
              justify-content: center;
            `}
          >
            <ActivityIndicator />
          </View>
        ) : messages.length === 0 ? (
          <EmptyView message="暂无消息" />
        ) : (
          <FlatList
            data={messages}
            keyExtractor={m => m.id}
            renderItem={renderItem}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={refresh} />
            }
            onEndReachedThreshold={0.5}
            onEndReached={hasMore ? loadMore : undefined}
            ListFooterComponent={
              loadingMore ? (
                <View
                  css={`
                    padding: ${rpx(16)}px 0;
                  `}
                >
                  <ActivityIndicator />
                </View>
              ) : (
                <View
                  css={`
                    height: ${rpx(16)}px;
                  `}
                />
              )
            }
            contentContainerStyle={{
              paddingTop: rpx(8),
              paddingLeft: rpx(8),
              paddingBottom: rpx(16),
            }}
          />
        )}
        <FootBumpUnreadOnLeave />
      </View>
    </ActionSheetProvider>
  );
});

// Reconcile the burger badge one more time when leaving the thread —
// catches the case where new messages arrived while we were inside.
const FootBumpUnreadOnLeave = memo(() => {
  const { refreshFeedUnread } = useFeedUnreadStore();
  useEffect(() => {
    return () => {
      void refreshFeedUnread();
    };
  }, [refreshFeedUnread]);
  return null;
});
